import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

// Pages
import HQLoginPage from "@/pages/HQLoginPage";
import WarehouseLoginPage from "@/pages/WarehouseLoginPage";
import StoreLoginPage from "@/pages/StoreLoginPage";

// HQ Pages
import HQLayout from "@/layouts/HQLayout";
import HQDashboard from "@/pages/hq/Dashboard";
import WarehousesPage from "@/pages/hq/Warehouses";
import StoresPage from "@/pages/hq/Stores";
import UsersPage from "@/pages/hq/Users";
import BrandsPage from "@/pages/hq/Brands";
import CategoriesPage from "@/pages/hq/Categories";
import UOMPage from "@/pages/hq/UOM";
import TaxPage from "@/pages/hq/Tax";
import ProductsPage from "@/pages/hq/Products";
import ProductEditPage from "@/pages/hq/ProductEdit";
import VendorsPage from "@/pages/hq/Vendors";
import OffersPage from "@/pages/hq/Offers";
import PurchaseOrdersPage from "@/pages/hq/PurchaseOrders";
import POCreatePage from "@/pages/hq/POCreate";
import GRNPage from "@/pages/hq/GRN";
import GRNEntryPage from "@/pages/hq/GRNEntry";
import VendorPaymentsPage from "@/pages/hq/VendorPayments";
import StockOverviewPage from "@/pages/hq/StockOverview";
import PriceSettingsPage from "@/pages/hq/PriceSettings";
import SyncStatusPage from "@/pages/hq/SyncStatus";
import StockTransfersPage from "@/pages/hq/StockTransfers";
import TransferCreatePage from "@/pages/hq/TransferCreate";
import StockAdjustmentsPage from "@/pages/hq/StockAdjustments";
import BusinessSettingsPage from "@/pages/hq/BusinessSettings";
import SystemSettingsPage from "@/pages/hq/SystemSettings";
import FixIssuesPage from "@/pages/hq/FixIssues";
import EntityResetPage from "@/pages/hq/EntityReset";
import {
  AllSalesReport,
  StoreWiseReport,
  CategoryWiseReport,
  BrandWiseReport,
  HsnWiseReport,
  GstTaxReport,
  FastMovingReport,
  SlowMovingReport,
  DeadStockReport,
} from "@/pages/hq/reports/SalesReports";

// HQ Purchase V2 Pages
import PurchaseOrderListPage from "@/pages/hq/purchase/PurchaseOrderList";
import CreatePurchaseOrderPage from "@/pages/hq/purchase/CreatePurchaseOrder";
import PurchaseOrderViewPage from "@/pages/hq/purchase/PurchaseOrderView";
import CreateGRNLocalPage from "@/pages/hq/purchase/CreateGRNLocal";
import CreateGRNInternationalPage from "@/pages/hq/purchase/CreateGRNInternational";
import PurchaseReturnsPage from "@/pages/hq/purchase/PurchaseReturns";

// Warehouse Pages
import WarehouseLayout from "@/layouts/WarehouseLayout";
import WarehouseDashboardPage from "@/pages/warehouse/Dashboard";
import WarehouseStockPage from "@/pages/warehouse/Stock";
import DamagedStockPage from "@/pages/warehouse/DamagedStock";
import GRNVerificationPage from "@/pages/warehouse/GRNVerification";
import WarehouseExpensesPage from "@/pages/warehouse/Expenses";
import WarehouseTransferCreatePage from "@/pages/warehouse/TransferCreate";
import WarehouseTransfersPage from "@/pages/warehouse/TransfersConsolidated";
import WarehouseTransferOrdersPage from "@/pages/warehouse/TransferOrders";
import WarehouseGRNEntryPage from "@/pages/warehouse/GRNEntry";
import WarehouseProductsPage from "@/pages/warehouse/Products";
import WarehouseStockRequestCreatePage from "@/pages/warehouse/StockRequestCreate";
import WarehouseStockRequestListPage from "@/pages/warehouse/StockRequestList";
import WarehouseStockRequestDetailPage from "@/pages/warehouse/StockRequestDetail";

// Store Pages
import StoreLayout from "@/layouts/StoreLayout";
import StoreDashboard from "@/pages/store/Dashboard";
import CashRegisterPage from "@/pages/store/CashRegister";
import POSBillingPage from "@/pages/store/POSBilling";
import SalesHistoryPage from "@/pages/store/SalesHistory";
import StoreStockPage from "@/pages/store/StoreStock";
import StockAuditPage from "@/pages/store/StockAudit";
import PublicAuditPage from "@/pages/public/PublicAuditPage";
import StoreIncomingTransfersPage from "@/pages/store/IncomingTransfers";
import StoreStockRequestsPage from "@/pages/store/StockRequests";
import StoreGRNEntryPage from "@/pages/store/GRNEntry";
import StoreInformationPage from "@/pages/store/StoreInformation";
import StoreExpensesPage from "@/pages/store/Expenses";
import StoreDamagedStockPage from "@/pages/store/DamagedStock";
import StoreTerminalsPage from "@/pages/store/Terminals";
import StoreSettingsPage from "@/pages/store/Settings";
import StoreCustomersPage from "@/pages/store/Customers";
import StoreLowStockSettingsPage from "@/pages/store/LowStockSettings";
import StoreLowStockAlertsPage from "@/pages/store/LowStockAlerts";
import StorePricingPage from "@/pages/store/StorePricing";
import StoreLocalVendorsPage from "@/pages/store/LocalVendors";
import StoreLocalVendorLedgerPage from "@/pages/store/LocalVendorLedger";
import StorePurchaseOrdersPage from "@/pages/store/StorePurchaseOrders";
import StoreVendorLedgerPage from "@/pages/store/StoreVendorLedger";
import ProductSalesReport from "@/pages/hq/reports/ProductSalesReport";
import ExpiryReport from "@/pages/hq/reports/ExpiryReport";
import ProfitAnalysisReport from "@/pages/hq/reports/ProfitAnalysisReport";
import DirectStockAdd from "@/pages/hq/DirectStockAdd";
import StoreHQPaymentsPage from "@/pages/store/HQPayments";
import OwnStoreLocalVendorsPage from "@/pages/store/OwnStoreLocalVendors";
import LocalPurchaseOrderPage from "@/pages/store/LocalPurchaseOrder";
import StoreGRNEntryMultiSource from "@/pages/store/StoreGRNEntryMultiSource";
import FranchiseBillingPage from "@/pages/store/FranchiseBilling";
import FranchiseSupplyPage from "@/pages/store/FranchiseSupply";
import StockTransferPage from "@/pages/store/StockTransfer";

// Local Store Pages
import LocalStoreLayout from "@/layouts/LocalStoreLayout";
import LocalPOS from "@/pages/local-store/LocalPOS";
import LocalCashRegister from "@/pages/local-store/LocalCashRegister";
import LocalSalesHistory from "@/pages/local-store/LocalSalesHistory";
import LocalStoreSettings from "@/pages/local-store/LocalStoreSettings";

// HQ Monitoring
import LiveSalesMonitoring from "@/pages/hq/LiveSalesMonitoring";
import StoreSyncStatus from "@/pages/hq/StoreSyncStatus";
import StoreHealthCheck from "@/pages/hq/StoreHealthCheck";
import StoreStocksPage from "@/pages/hq/StoreStocks";
import ProductWeightsPage from "@/pages/hq/ProductWeights";
import SalesStockRepairPage from "@/pages/hq/SalesStockRepair";
import PaymentModeReport from "@/pages/hq/reports/PaymentModeReport";
import BackupPage from "@/pages/hq/Backup";
import SecurityCodePage from "@/pages/hq/SecurityCode";
import MarginChangeLogPage from "@/pages/hq/MarginChangeLog";
import HQCustomersPage from "@/pages/hq/Customers";
import SyncDashboard from "@/pages/hq/SyncDashboard";

// Revenue Management
import ManufacturersPage from "@/pages/hq/Manufacturers";
import RevenueSettingsPage from "@/pages/hq/RevenueSettings";
import ReceivableInvoicesPage from "@/pages/hq/ReceivableInvoices";
import ReceivablePaymentsPage from "@/pages/hq/ReceivablePayments";
import StoreRackSettingsPage from "@/pages/hq/StoreRackSettings";

// Booking Management
import ShelfBookingPage from "@/pages/hq/ShelfBooking";
import BrandStaffContractsPage from "@/pages/hq/BrandStaffContracts";
import BookingSettingsPage from "@/pages/hq/BookingSettings";

// E-commerce Settings
import LocationSettingsPage from "@/pages/hq/LocationSettings";
import DeliveryZonesPage from "@/pages/hq/DeliveryZones";
import LogisticsSettingsPage from "@/pages/hq/LogisticsSettings";
import StoreEcommerceSettingsPage from "@/pages/hq/StoreEcommerceSettings";
import EcommerceOrdersPage from "@/pages/hq/EcommerceOrders";
import CMSHomePage from "@/pages/hq/CMSHome";
import LegalPagesPage from "@/pages/hq/LegalPages";

// Expenses
import ExpenseHeadsPage from "@/pages/hq/ExpenseHeads";
import ExpensesManagerPage from "@/pages/hq/ExpensesManager";
import StoreExpensesMonitorPage from "@/pages/hq/StoreExpensesMonitor";

// Asset Storage
import AssetStorageSettingsPage from "@/pages/hq/AssetStorageSettings";

// Stock Transfers & Ledgers
import StockRequestsPage from "@/pages/hq/StockRequests";
import TransferOrdersPage from "@/pages/hq/TransferOrders";
import FranchiseLedgerPage from "@/pages/hq/FranchiseLedger";
import VendorLedgerPage from "@/pages/hq/VendorLedger";
import VendorStatementPage from "@/pages/hq/VendorStatement";
import DebitCreditNotesPage from "@/pages/hq/DebitCreditNotes";
import FranchisePaymentsPage from "@/pages/hq/FranchisePayments";
import FranchisePricingLevelsPage from "@/pages/hq/FranchisePricingLevels";
import LocalVendorMonitoringPage from "@/pages/hq/LocalVendorMonitoring";
import FranchisePOApprovalsPage from "@/pages/hq/FranchisePOApprovals";
import FranchiseDirectPOMonitoringPage from "@/pages/hq/FranchiseDirectPOMonitoring";

// Protected Route Component
const ProtectedRoute = ({ children, allowedEntityTypes = [] }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedEntityTypes.length > 0 && !allowedEntityTypes.includes(user?.entity_type)) {
    // Redirect to appropriate dashboard based on entity type
    if (user?.entity_type === 'hq') return <Navigate to="/hq" replace />;
    if (user?.entity_type === 'warehouse') return <Navigate to="/warehouse" replace />;
    if (user?.entity_type === 'store') return <Navigate to="/store" replace />;
  }

  return children;
};

// HQ Routes
const HQRoutes = () => (
  <ProtectedRoute allowedEntityTypes={['hq']}>
    <HQLayout>
      <Routes>
        <Route index element={<HQDashboard />} />
        <Route path="warehouses" element={<WarehousesPage />} />
        <Route path="stores" element={<StoresPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="brands" element={<BrandsPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="uom" element={<UOMPage />} />
        <Route path="tax" element={<TaxPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/new" element={<ProductEditPage />} />
        <Route path="products/:id/edit" element={<ProductEditPage />} />
        <Route path="vendors" element={<VendorsPage />} />
        <Route path="manufacturers" element={<ManufacturersPage />} />
        <Route path="revenue/settings" element={<RevenueSettingsPage />} />
        <Route path="revenue/invoices" element={<ReceivableInvoicesPage />} />
        <Route path="revenue/payments" element={<ReceivablePaymentsPage />} />
        <Route path="stores/:storeId/rack-settings" element={<StoreRackSettingsPage />} />
        <Route path="bookings/shelf" element={<ShelfBookingPage />} />
        <Route path="bookings/staff" element={<BrandStaffContractsPage />} />
        <Route path="bookings/settings" element={<BookingSettingsPage />} />
        <Route path="ecommerce/locations" element={<LocationSettingsPage />} />
        <Route path="ecommerce/delivery-zones" element={<DeliveryZonesPage />} />
        <Route path="ecommerce/logistics" element={<LogisticsSettingsPage />} />
        <Route path="ecommerce/store-settings" element={<StoreEcommerceSettingsPage />} />
        <Route path="ecommerce/orders" element={<EcommerceOrdersPage />} />
        <Route path="ecommerce/cms-home" element={<CMSHomePage />} />
        <Route path="ecommerce/legal-pages" element={<LegalPagesPage />} />
        <Route path="expenses/categories" element={<ExpenseHeadsPage />} />
        <Route path="expenses" element={<ExpensesManagerPage />} />
        <Route path="expenses/store-expenses" element={<StoreExpensesMonitorPage />} />
        <Route path="settings/asset-storage" element={<AssetStorageSettingsPage />} />
        <Route path="offers" element={<OffersPage />} />
        <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="purchase-orders/new" element={<POCreatePage />} />
        <Route path="purchase-orders/:id/edit" element={<POCreatePage />} />
        <Route path="grn" element={<GRNPage />} />
        <Route path="grn/new" element={<GRNEntryPage />} />
        <Route path="grn/:id/edit" element={<GRNEntryPage />} />
        {/* New Purchase V2 Routes */}
        <Route path="purchase/orders" element={<PurchaseOrderListPage />} />
        <Route path="purchase/orders/new" element={<CreatePurchaseOrderPage />} />
        <Route path="purchase/orders/:id" element={<PurchaseOrderViewPage />} />
        <Route path="purchase/orders/:id/edit" element={<CreatePurchaseOrderPage />} />
        <Route path="purchase/grn" element={<GRNPage />} />
        <Route path="purchase/grn/local/new" element={<CreateGRNLocalPage />} />
        <Route path="purchase/grn/local/:id/edit" element={<CreateGRNLocalPage />} />
        <Route path="purchase/grn/international/new" element={<CreateGRNInternationalPage />} />
        <Route path="purchase/grn/international/:id/edit" element={<CreateGRNInternationalPage />} />
        <Route path="purchase/returns" element={<PurchaseReturnsPage />} />
        <Route path="vendor-payments" element={<VendorPaymentsPage />} />
        <Route path="inventory/stock" element={<StockOverviewPage />} />
        <Route path="inventory/store-stocks" element={<StoreStocksPage />} />
        <Route path="inventory/product-weights" element={<ProductWeightsPage />} />
        <Route path="inventory/sales-stock-repair" element={<SalesStockRepairPage />} />
        <Route path="inventory/price-settings" element={<PriceSettingsPage />} />
        <Route path="inventory/transfers" element={<StockTransfersPage />} />
        <Route path="inventory/transfers/new" element={<TransferCreatePage />} />
        <Route path="inventory/adjustments" element={<StockAdjustmentsPage />} />
        <Route path="inventory/stock-requests" element={<StockRequestsPage />} />
        <Route path="inventory/transfer-orders" element={<TransferOrdersPage />} />
        <Route path="inventory/*" element={<ComingSoon title="Inventory" />} />
        <Route path="ledgers/franchise" element={<FranchiseLedgerPage />} />
        <Route path="ledgers/vendor" element={<VendorLedgerPage />} />
        <Route path="ledgers/vendor/:vendorId/statement" element={<VendorStatementPage />} />
        <Route path="ledgers/notes" element={<DebitCreditNotesPage />} />
        <Route path="payments/franchise" element={<FranchisePaymentsPage />} />
        <Route path="franchise/pricing-levels" element={<FranchisePricingLevelsPage />} />
        <Route path="franchise/local-vendors" element={<LocalVendorMonitoringPage />} />
        <Route path="franchise/direct-po" element={<FranchiseDirectPOMonitoringPage />} />
        <Route path="franchise/po-approvals" element={<FranchisePOApprovalsPage />} />
        <Route path="monitoring/live-sales" element={<LiveSalesMonitoring />} />
          <Route path="monitoring/sync-status" element={<StoreSyncStatus />} />
          <Route path="monitoring/health-check" element={<StoreHealthCheck />} />
        <Route path="sync-status" element={<SyncStatusPage />} />
        <Route path="sync-dashboard" element={<SyncDashboard />} />
        <Route path="settings/business" element={<BusinessSettingsPage />} />
        <Route path="settings/system" element={<SystemSettingsPage />} />
        <Route path="settings/fix-issues" element={<FixIssuesPage />} />
        <Route path="settings/entity-reset" element={<EntityResetPage />} />
        <Route path="stock-audit" element={<StockAuditPage scope="hq" />} />
        <Route path="backup" element={<BackupPage />} />
        <Route path="reports/sales/product-wise" element={<ProductSalesReport scope="hq" />} />
        <Route path="reports/expiry" element={<ExpiryReport scope="hq" />} />
        <Route path="reports/profit" element={<ProfitAnalysisReport />} />
        <Route path="inventory/direct-stock" element={<DirectStockAdd />} />
        <Route path="settings/security-code" element={<SecurityCodePage />} />
        <Route path="margin-log" element={<MarginChangeLogPage />} />
        <Route path="customers" element={<HQCustomersPage />} />
        <Route path="reports/sales/all" element={<AllSalesReport />} />
        <Route path="reports/sales/payment-mode" element={<PaymentModeReport />} />
        <Route path="reports/sales/store-wise" element={<StoreWiseReport />} />
        <Route path="reports/sales/category-wise" element={<CategoryWiseReport />} />
        <Route path="reports/sales/brand-wise" element={<BrandWiseReport />} />
        <Route path="reports/sales/hsn-wise" element={<HsnWiseReport />} />
        <Route path="reports/sales/gst-tax" element={<GstTaxReport />} />
        <Route path="reports/sales/fast-moving" element={<FastMovingReport />} />
        <Route path="reports/sales/slow-moving" element={<SlowMovingReport />} />
        <Route path="reports/sales/dead-stock" element={<DeadStockReport />} />
        <Route path="reports/*" element={<ComingSoon title="Reports" />} />
        <Route path="settings" element={<ComingSoon title="Settings" />} />
      </Routes>
    </HQLayout>
  </ProtectedRoute>
);

// Coming Soon placeholder
const ComingSoon = ({ title }) => (
  <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
      <div className="w-24 h-24 rounded-full bg-orange-100 flex items-center justify-center mb-6">
        <span className="text-4xl">🚧</span>
      </div>
      <h1 className="text-2xl font-bold text-gray-800 mb-2">{title}</h1>
      <p className="text-gray-500 max-w-md">
        This module is under development and will be available in the next phase.
      </p>
    </div>
  </div>
);

// Warehouse Routes
const WarehouseRoutes = () => (
  <ProtectedRoute allowedEntityTypes={['warehouse', 'hq']}>
    <WarehouseLayout>
      <Routes>
        <Route index element={<WarehouseDashboardPage />} />
        <Route path="stock" element={<WarehouseStockPage />} />
        <Route path="stock/adjustments" element={<StockAdjustmentsPage />} />
        <Route path="stock/damaged" element={<DamagedStockPage />} />
        <Route path="grn/pending" element={<GRNVerificationPage />} />
        <Route path="grn/verify" element={<GRNVerificationPage />} />
        <Route path="grn/history" element={<GRNVerificationPage />} />
        <Route path="grn/entry" element={<WarehouseGRNEntryPage />} />
        <Route path="products" element={<WarehouseProductsPage />} />
        <Route path="stock-requests" element={<WarehouseStockRequestListPage />} />
        <Route path="stock-requests/new" element={<WarehouseStockRequestCreatePage />} />
        <Route path="stock-requests/:id" element={<WarehouseStockRequestDetailPage />} />
        <Route path="transfer-orders" element={<WarehouseTransferOrdersPage />} />
        <Route path="transfers" element={<WarehouseTransfersPage />} />
        <Route path="transfers/outgoing" element={<WarehouseTransfersPage />} />
        <Route path="transfers/pending" element={<WarehouseTransfersPage />} />
        <Route path="transfers/dispatch" element={<WarehouseTransfersPage />} />
        <Route path="transfers/transit" element={<WarehouseTransfersPage />} />
        <Route path="transfers/completed" element={<WarehouseTransfersPage />} />
        <Route path="transfers/returns" element={<WarehouseTransfersPage />} />
        <Route path="transfers/new" element={<WarehouseTransferCreatePage />} />
        <Route path="transfers/history" element={<WarehouseTransfersPage />} />
        <Route path="expenses" element={<WarehouseExpensesPage />} />
        <Route path="reports/*" element={<ComingSoon title="Reports" />} />
        <Route path="*" element={<ComingSoon title="Warehouse Module" />} />
      </Routes>
    </WarehouseLayout>
  </ProtectedRoute>
);

// Store POS Routes
const StoreRoutes = () => {
  const { user } = useAuth();
  
  // Check if user is cashier (has only register and pos permissions)
  const userPermissions = user?.permissions || [];
  const isCashier = userPermissions.length > 0 && userPermissions.length <= 2 &&
    userPermissions.some(p => (typeof p === 'string' ? p : p?.module) === 'register' || (typeof p === 'string' ? p : p?.module) === 'pos');
  
  const defaultRoute = isCashier ? 'pos' : 'dashboard';
  
  return (
    <ProtectedRoute allowedEntityTypes={['store', 'hq']}>
      <StoreLayout>
        <Routes>
          <Route index element={<Navigate to={defaultRoute} replace />} />
          <Route path="dashboard" element={<StoreDashboard />} />
          <Route path="pos" element={<POSBillingPage />} />
          <Route path="register" element={<CashRegisterPage />} />
          <Route path="sales" element={<SalesHistoryPage />} />
          <Route path="customers" element={<StoreCustomersPage />} />
          <Route path="stock" element={<StoreStockPage />} />
          <Route path="stock-audit" element={<StockAuditPage scope="store" />} />
          <Route path="stock-requests" element={<StoreStockRequestsPage />} />
          <Route path="grn" element={<StoreGRNEntryPage />} />
          <Route path="transfers" element={<StoreIncomingTransfersPage />} />
          <Route path="low-stock/settings" element={<StoreLowStockSettingsPage />} />
          <Route path="low-stock/alerts" element={<StoreLowStockAlertsPage />} />
          <Route path="pricing" element={<StorePricingPage />} />
          <Route path="local-vendors" element={<StoreLocalVendorsPage />} />
          <Route path="local-vendors/:vendorId/ledger" element={<StoreLocalVendorLedgerPage />} />
          <Route path="hq-local-vendors" element={<OwnStoreLocalVendorsPage />} />
          <Route path="local-purchase" element={<LocalPurchaseOrderPage />} />
          <Route path="local-purchase/new" element={<LocalPurchaseOrderPage />} />
          <Route path="grn-entry" element={<StoreGRNEntryMultiSource />} />
          <Route path="franchise-billing" element={<FranchiseBillingPage />} />
          <Route path="franchise-supply" element={<FranchiseSupplyPage />} />
          <Route path="stock-transfer" element={<StockTransferPage />} />
          <Route path="purchase-orders" element={<StorePurchaseOrdersPage />} />
          <Route path="vendor-ledger" element={<StoreVendorLedgerPage />} />
          <Route path="reports/sales" element={<ProductSalesReport scope="store" />} />
          <Route path="reports/expiry" element={<ExpiryReport scope="store" />} />
          <Route path="hq-payments" element={<StoreHQPaymentsPage />} />
          <Route path="expenses" element={<StoreExpensesPage />} />
          <Route path="damaged-stock" element={<StoreDamagedStockPage />} />
          <Route path="terminals" element={<StoreTerminalsPage />} />
          <Route path="settings" element={<StoreSettingsPage />} />
          <Route path="info" element={<StoreInformationPage />} />
          <Route path="reports/*" element={<ComingSoon title="Reports" />} />
          <Route path="*" element={<ComingSoon title="Store Module" />} />
        </Routes>
      </StoreLayout>
    </ProtectedRoute>
  );
};

// Root redirect based on user type
const RootRedirect = () => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect based on entity type
  if (user?.entity_type === 'hq') return <Navigate to="/hq" replace />;
  if (user?.entity_type === 'warehouse') return <Navigate to="/warehouse" replace />;
  if (user?.entity_type === 'store') return <Navigate to="/store" replace />;

  return <Navigate to="/login" replace />;
};

// Local Store Routes (connects to local server)
const LocalStoreRoutes = () => (
  <Routes>
    <Route element={<LocalStoreLayout />}>
      <Route index element={<Navigate to="/local-store/pos" replace />} />
      <Route path="pos" element={<LocalPOS />} />
      <Route path="register" element={<LocalCashRegister />} />
      <Route path="sales" element={<LocalSalesHistory />} />
    </Route>
    <Route path="settings" element={<LocalStoreSettings />} />
  </Routes>
);

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public Routes - Separate Login Pages */}
            <Route path="/login" element={<HQLoginPage />} />
            <Route path="/login/warehouse" element={<WarehouseLoginPage />} />
            <Route path="/login/store" element={<StoreLoginPage />} />

            {/* Public staff stock-count link (PIN protected) */}
            <Route path="/audit/:auditId" element={<PublicAuditPage />} />

            {/* Root redirect */}
            <Route path="/" element={<RootRedirect />} />

            {/* HQ Dashboard Routes */}
            <Route path="/hq/*" element={<HQRoutes />} />

            {/* Warehouse Dashboard Routes */}
            <Route path="/warehouse/*" element={<WarehouseRoutes />} />

            {/* Store POS Routes */}
            <Route path="/store/*" element={<StoreRoutes />} />

            {/* Local Store Routes (Offline-capable) */}
            <Route path="/local-store/*" element={<LocalStoreRoutes />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
